import React, { useState } from 'react';
import { X, Download, ChevronLeft, ChevronRight, FileText, Image } from 'lucide-react';

const CertificateViewer = ({ isOpen, onClose, language, isDarkMode }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const certificates = [
    {
      name: language === 'es' ? 'Certificado JaP' : 'JaP Certificate',
      file: '/docs/JaP - Ines Vaz.png',
      type: 'image'
    },
    {
      name: language === 'es' ? 'Certificado FCE B2' : 'FCE B2 Certificate',
      file: '/docs/fce certificate b2.jpeg',
      type: 'image'
    },
    {
      name: language === 'es' ? 'Certificado IBM Full Stack' : 'IBM Full Stack Certificate',
      file: '/docs/IBM full stack certificate.pdf',
      type: 'pdf'
    },
    {
      name: language === 'es' ? 'Certificado IBM Data Science' : 'IBM Data Science Certificate',
      file: '/docs/ibmdatascience.pdf',
      type: 'pdf'
    },
    {
      name: language === 'es' ? 'Certificado IBM Data Engineering Foundations' : 'IBM Data Engineering Foundations Certificate',
      file: '/docs/dataengineering-foundations.pdf',
      type: 'pdf'
    },
    {
      name: language === 'es' ? 'Certificado Scrum Master' : 'Scrum Master Certificate',
      file: '/docs/scrum master.pdf',
      type: 'pdf'
    },
    {
      name: language === 'es' ? 'Certificado Google Cloud' : 'Google Cloud Certificate',
      file: '/docs/certificado-google.png',
      type: 'image'
    }
  ];

  const nextCertificate = () => {
    setCurrentIndex((prev) => (prev + 1) % certificates.length);
  };

  const prevCertificate = () => {
    setCurrentIndex((prev) => (prev - 1 + certificates.length) % certificates.length);
  };

  const downloadCertificate = () => {
    const link = document.createElement('a');
    link.href = certificates[currentIndex].file;
    link.download = certificates[currentIndex].name;
    link.click();
  };

  if (!isOpen) return null;

  const currentCert = certificates[currentIndex];
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            {currentCert.type === 'pdf' ? (
              <FileText className="w-6 h-6 text-red-600" />
            ) : (
              <Image className="w-6 h-6 text-red-600" />
            )}
            <h3 className={`text-xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              {currentCert.name}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadCertificate}
              className={`p-2 transition-colors ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-red-400' 
                  : 'text-gray-600 hover:text-red-600'
              }`}
              title={language === 'es' ? 'Descargar' : 'Download'}
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className={`p-2 transition-colors ${
                isDarkMode 
                  ? 'text-gray-400 hover:text-red-400' 
                  : 'text-gray-600 hover:text-red-600'
              }`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>        {/* Content */}
        <div className={`relative h-96 md:h-[500px] flex items-center justify-center ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          {currentCert.type === 'pdf' ? (
            <iframe
              src={currentCert.file}
              className="w-full h-full"
              title={currentCert.name}
              style={{ border: 'none' }}
            />
          ) : (
            <img
              src={currentCert.file}
              alt={currentCert.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                console.error('Error loading image:', currentCert.file);
                e.target.style.display = 'none';
              }}
            />
          )}

          {/* Navigation Arrows */}
          {certificates.length > 1 && (
            <>
              <button
                onClick={prevCertificate}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextCertificate}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>        {/* Footer with thumbnails */}
        <div className={`p-4 border-t ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex justify-center gap-2 overflow-x-auto">
            {certificates.map((cert, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`flex-shrink-0 p-2 rounded-lg border-2 transition-all ${
                  index === currentIndex
                    ? 'border-red-600 bg-red-50 dark:bg-red-900/20'
                    : `border-gray-300 hover:border-red-400 ${
                        isDarkMode ? 'dark:border-gray-600' : ''
                      }`
                }`}
              >
                <div className="flex items-center gap-2">
                  {cert.type === 'pdf' ? (
                    <FileText className="w-4 h-4 text-red-600" />
                  ) : (
                    <Image className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-xs font-medium max-w-20 truncate ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {cert.name}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className={`text-center mt-3 text-sm ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {currentIndex + 1} / {certificates.length}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateViewer;
